// Passe da Temporada (lote 3, item 14). Acompanha a temporada da liga (trimestral; a primeira vai até 31/12/2026),
// com 30 níveis de 10 estrelas. Estrelas: 1 por partida terminada, 2 por missão do dia e 5 por missão da semana.
// Duas trilhas: a grátis, para todos (Rodagem, alguns cosméticos e Tentos dentro do teto do mês), e a do assinante,
// incluída no Prata e no Ouro (cosméticos exclusivos da temporada, descansos extras e mais Rodagem). Quem assina no
// meio da temporada libera na hora o que já alcançou, uma vez só. A trilha do assinante não é vendida à parte.
// Os prêmios ficam na tabela passe_premios (seed por migração; estes são os mesmos): '*' vale para a temporada que
// não tem prêmios próprios.
import type { Plano } from '@truco6/planos';
import type { Recompensa } from './recompensas.ts';

export const VALORES_PASSE = {
  passe_niveis: 30,
  passe_estrelas_nivel: 10,
  passe_estrela_partida: 1,
  passe_estrelas_missao_dia: 2,
  passe_estrelas_missao_semana: 5,
} as const;
export type ValoresPasse = Record<keyof typeof VALORES_PASSE, number>;

export const TRILHAS = ['gratis', 'assinante'] as const;
export type Trilha = (typeof TRILHAS)[number];
export type TipoDePremio = 'rodagem' | 'tentos' | 'cosmetico' | 'descanso';

export interface PremioDoPasse { temporada: string; nivel: number; trilha: Trilha; tipo: TipoDePremio; valor: number; cosmetico: string | null }

// Os itens do passe de 2026-T4 (catálogo de cosméticos, origem passe).
const ITENS_2026_T4: Record<string, { trilha: Trilha; cosmetico: string }> = {
  12: { trilha: 'gratis', cosmetico: 'moldura_brasa' },
  10: { trilha: 'assinante', cosmetico: 'moldura_fogueira' },
  20: { trilha: 'assinante', cosmetico: 'verso_fogueira' },
  30: { trilha: 'assinante', cosmetico: 'mesa_fogueira' },
};

// Trilha grátis: Rodagem em todo nível (o dobro de 5 em 5) e 5 Tentos nos níveis 10, 20 e 30.
// Trilha do assinante: mais Rodagem, um descanso nos níveis 5, 15 e 25 e os cosméticos da temporada.
// Sem itens próprios ('*'), o nível do cosmético rende Rodagem.
export function premiosDoPasse(temporada: string): PremioDoPasse[] {
  const out: PremioDoPasse[] = [], itens = temporada === '2026-T4' ? ITENS_2026_T4 : {};
  const p = (nivel: number, trilha: Trilha, tipo: TipoDePremio, valor: number, cosmetico: string | null = null) =>
    out.push({ temporada, nivel, trilha, tipo, valor, cosmetico });
  for (let n = 1; n <= VALORES_PASSE.passe_niveis; n++) {
    const g = itens[n]?.trilha === 'gratis' ? itens[n] : undefined, a = itens[n]?.trilha === 'assinante' ? itens[n] : undefined;
    if (n % 10 === 0) p(n, 'gratis', 'tentos', 5);
    else if (g) p(n, 'gratis', 'cosmetico', 1, g.cosmetico);
    else p(n, 'gratis', 'rodagem', n % 5 === 0 ? 60 : 30);
    if (a) p(n, 'assinante', 'cosmetico', 1, a.cosmetico);
    else if (n === 5 || n === 15 || n === 25) p(n, 'assinante', 'descanso', 1);
    else p(n, 'assinante', 'rodagem', n % 10 === 0 ? 150 : 60);
  }
  return out;
}
export const PREMIOS_DO_PASSE: readonly PremioDoPasse[] = [...premiosDoPasse('*'), ...premiosDoPasse('2026-T4')];

// Os prêmios de uma temporada: os dela, ou os de '*'.
export function premiosDaTemporada(todos: readonly PremioDoPasse[], temporada: string): PremioDoPasse[] {
  const dela = todos.filter((x) => x.temporada === temporada);
  return dela.length ? dela : todos.filter((x) => x.temporada === '*');
}

export const assinaPasse = (plano: Plano) => plano === 'prata' || plano === 'ouro';

export function estrelasDaPartida(missoesDoDia: number, missoesDaSemana: number, v: ValoresPasse): number {
  return v.passe_estrela_partida + missoesDoDia * v.passe_estrelas_missao_dia + missoesDaSemana * v.passe_estrelas_missao_semana;
}

export function nivelDoPasse(estrelas: number, v: ValoresPasse): number {
  return Math.min(v.passe_niveis, Math.floor(Math.max(0, estrelas) / Math.max(1, v.passe_estrelas_nivel)));
}

// Estado de um jogador na temporada: as estrelas e até que nível cada trilha já pagou.
export interface EstadoPasse { estrelas: number; gratisAte: number; assinanteAte: number }
export const PASSE_INICIAL: EstadoPasse = { estrelas: 0, gratisAte: 0, assinanteAte: 0 };

// Os prêmios a pagar agora: a grátis até o nível alcançado e, para quem assina, a do assinante também (inclusive o
// que já tinha alcançado antes de assinar). Cada prêmio tem a sua chave: paga uma vez só.
export function premiosAPagar(e: EstadoPasse, nivel: number, plano: Plano, premios: readonly PremioDoPasse[]): { premios: PremioDoPasse[]; estado: EstadoPasse } {
  const assina = assinaPasse(plano);
  const out = premios.filter((x) => (x.trilha === 'gratis' && x.nivel > e.gratisAte && x.nivel <= nivel)
    || (assina && x.trilha === 'assinante' && x.nivel > e.assinanteAte && x.nivel <= nivel));
  return { premios: out, estado: { ...e, gratisAte: Math.max(e.gratisAte, nivel), assinanteAte: assina ? Math.max(e.assinanteAte, nivel) : e.assinanteAte } };
}

// O prêmio como recompensa do motor: Rodagem ou Tentos (no teto do mês). Cosmético e descanso entram com Rodagem e
// Tentos zerados, só para a chave (quem aplica o item ou o descanso é o armazém, se a chave for nova).
export function recompensaDoPremio(usuario: string, x: PremioDoPasse, temporada: string, em: number): Recompensa {
  return {
    usuario, motivo: 'passe', chave: `passe:${usuario}:${temporada}:${x.trilha}:${x.nivel}`, anonimo: false, em, teto: x.tipo === 'tentos',
    rodagem: x.tipo === 'rodagem' ? x.valor : 0, tentos: x.tipo === 'tentos' ? x.valor : 0,
    detalhes: { temporada, nivel: x.nivel, trilha: x.trilha, tipo: x.tipo, ...(x.cosmetico ? { cosmetico: x.cosmetico } : {}) },
  };
}

// Para a tela do passe e o fim de partida.
export interface PremioNaTela { tipo: TipoDePremio; valor: number; cosmetico: { id: string; nome: string; categoria: string } | null; pago: boolean }
export interface PainelPasse {
  temporada: string;
  fim: number; // última hora da temporada
  estrelas: number;
  nivel: number;
  niveis: number; // 30
  estrelasPorNivel: number;
  assinante: boolean; // Prata ou Ouro: as duas trilhas
  trilhas: { nivel: number; gratis: PremioNaTela[]; assinante: PremioNaTela[] }[];
}
export interface PasseNoFim { temporada: string; estrelas: number; total: number; nivel: { antes: number; depois: number }; niveis: number; estrelasPorNivel: number }

export function passeNaTela(
  temporada: string, fim: number, e: EstadoPasse, plano: Plano, premios: readonly PremioDoPasse[], v: ValoresPasse,
  nomeDoCosmetico: (id: string) => { nome: string; categoria: string } | undefined,
): PainelPasse {
  const naTela = (x: PremioDoPasse, ate: number): PremioNaTela => ({
    tipo: x.tipo, valor: x.valor, pago: x.nivel <= ate,
    cosmetico: x.cosmetico ? { id: x.cosmetico, ...(nomeDoCosmetico(x.cosmetico) ?? { nome: x.cosmetico, categoria: 'moldura' }) } : null,
  });
  const trilhas = Array.from({ length: v.passe_niveis }, (_, i) => {
    const nivel = i + 1, doNivel = premios.filter((x) => x.nivel === nivel);
    return {
      nivel,
      gratis: doNivel.filter((x) => x.trilha === 'gratis').map((x) => naTela(x, e.gratisAte)),
      assinante: doNivel.filter((x) => x.trilha === 'assinante').map((x) => naTela(x, e.assinanteAte)),
    };
  });
  return {
    temporada, fim, estrelas: e.estrelas, nivel: nivelDoPasse(e.estrelas, v), niveis: v.passe_niveis, estrelasPorNivel: v.passe_estrelas_nivel,
    assinante: assinaPasse(plano), trilhas,
  };
}
